/**
 * listCount - "Showing 51–100 of 1037 matters", in one place.
 *
 * This sentence used to be computed inside `Table`, because that is where it
 * was drawn. It is drawn in the FILTER BAR now - the bar is where you narrow a
 * list, so it is where the size of what you narrowed it to belongs - and the
 * bar is a SIBLING of the table, not its parent. Neither can reach the other's
 * props, so the arithmetic moved to a function both can call and the page
 * passes the numbers to whichever draws it.
 *
 * THE RANGE MATTERS. On a paged list "Showing 50 of 1037" is wrong twice over:
 * page 3 is not showing the first fifty, and the reader has no idea where in
 * the set they are. With a page and a size it reads as the span it actually
 * covers; without them it falls back to a plain count, which is right for a
 * list that loads everything at once.
 */

/** Just enough to describe what is on screen. */
export interface ListCountInput {
  /** Rows rendered right now. */
  showing?: number | null;
  /** Rows the query matches in total. */
  total?: number | null;
  /** i18n KEY for the noun - `matters`, `reviews`. Defaults to `items`. */
  label?: string;
  /** Appends "| N selected" when anything is ticked. */
  selected?: number | null;
  /** 1-based. With `pageSize`, turns the count into a range. */
  page?: number | null;
  pageSize?: number | null;
}

/** Whether there is anything to say at all. */
export const hasListCount = (input: ListCountInput): boolean =>
  input.total != null && input.showing != null;

/**
 * The sentence, translated through the caller's `t`.
 *
 * Returns '' when the numbers are not both known - a caller that renders this
 * blindly gets nothing rather than "Showing undefined of undefined".
 */
export const listCountText = (
  input: ListCountInput,
  t: (key: string) => string,
): string => {
  if (!hasListCount(input)) return '';

  const { showing, total, page, pageSize } = input;
  const noun = t(input.label || 'items');
  const selected =
    input.selected != null && input.selected > 0
      ? ` | ${input.selected} ${t('selected')}`
      : '';

  // No paging, or an empty page: a plain count is the honest reading.
  if (page == null || pageSize == null || !showing) {
    return `${t('Showing')} ${showing} ${t('of')} ${total} ${noun}${selected}`;
  }

  const size = pageSize || showing;
  const start = (Math.max(page, 1) - 1) * size + 1;
  const end = start + showing - 1;
  return `${t('Showing')} ${start}–${end} ${t('of')} ${total} ${noun}${selected}`;
};
